import { useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { auditService } from '../services/auditService';
import './AuditScheduleConflicts.css';

/** Stable text key for a list of ids, so a new array with the same ids is "the same". */
function idsKey(ids) {
  return (ids || []).map(String).sort().join(',');
}

function AuditScheduleConflicts({
  startAt,
  endAt,
  teamIds,
  dynamicMemberIds,
  excludeAuditId,
  onConflictsChange,
  isEditing = false,
}) {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  // The parent form redraws often (and passes new arrays each time); only a
  // real change of dates, teams or members should trigger a new check.
  const teamKey = idsKey(teamIds);
  const memberKey = idsKey(dynamicMemberIds);
  const hasSchedule = Boolean(startAt && endAt);
  const hasTeams = teamKey !== '' || memberKey !== '';

  const notifyRef = useRef(onConflictsChange);
  notifyRef.current = onConflictsChange;
  const lastSentRef = useRef('[]');
  const requestRef = useRef(0);

  const publish = (next) => {
    const signature = JSON.stringify(next);
    if (signature !== lastSentRef.current) {
      lastSentRef.current = signature;
      notifyRef.current?.(next);
    }
  };

  useEffect(() => {
    if (!hasSchedule || !hasTeams) {
      setConflicts([]);
      setChecked(false);
      setLoading(false);
      publish([]);
      return undefined;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await auditService.previewConflicts({
          start_at: startAt,
          end_at: endAt,
          team_ids: teamKey ? teamKey.split(',').map(Number) : [],
          dynamic_member_ids: memberKey ? memberKey.split(',').map(Number) : [],
          exclude_audit_id: excludeAuditId || null,
        });
        if (requestRef.current !== requestId) return; // a newer check started
        const next = data || [];
        setConflicts(next);
        setChecked(true);
        publish(next);
      } catch {
        if (requestRef.current !== requestId) return;
        setConflicts([]);
        setChecked(true);
        publish([]);
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
    // publish only reads refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAt, endAt, teamKey, memberKey, excludeAuditId, hasSchedule, hasTeams]);

  if (!hasSchedule) {
    return (
      <div className="audit-schedule-conflicts audit-schedule-conflicts--muted">
        Set the new schedule to check for overlapping team assignments.
      </div>
    );
  }

  if (!hasTeams) {
    return (
      <div className="audit-schedule-conflicts audit-schedule-conflicts--muted">
        Select teams or dynamic members to check for schedule clashes.
      </div>
    );
  }

  if (!checked) {
    return (
      <div className="audit-schedule-conflicts audit-schedule-conflicts--loading">
        Checking team schedules...
      </div>
    );
  }

  const refreshing = loading ? (
    <span className="audit-schedule-conflicts__refreshing">Re-checking…</span>
  ) : null;

  if (conflicts.length === 0) {
    return (
      <div className="audit-schedule-conflicts audit-schedule-conflicts--clear">
        No schedule conflicts detected for the selected teams and dates. {refreshing}
      </div>
    );
  }

  const uniqueMembers = new Set(conflicts.map((item) => item.user_id)).size;

  return (
    <div className="audit-schedule-conflicts audit-schedule-conflicts--warn">
      <header>
        <strong>
          {uniqueMembers} member{uniqueMembers === 1 ? '' : 's'} with schedule
          clashes
        </strong>
        <span>Overlapping with another schedule in this period {refreshing}</span>
      </header>

      <ul>
        {conflicts.map((item) => (
          <li key={`${item.team_id}-${item.user_id}`}>
            <div className="audit-schedule-conflicts__person">
              <strong>{item.user_name}</strong>
              <span>
                {item.team_name} · {item.user_email}
              </span>
            </div>
            <div className="audit-schedule-conflicts__audits">
              {(item.conflicting_audits || []).map((audit) => (
                <p key={`${audit.audit_id}-${audit.team_id}`}>
                  Also on <strong>{audit.audit_name}</strong> ({audit.team_name}
                  ) · {format(parseISO(audit.start_at), 'MMM d, HH:mm')} –{' '}
                  {format(parseISO(audit.end_at), 'MMM d, HH:mm')}
                </p>
              ))}
            </div>
          </li>
        ))}
      </ul>

      <p className="audit-schedule-conflicts__hint">
        {isEditing
          ? 'Use member adjustments below to remove or replace people for this schedule only.'
          : 'Save the schedule, then use member adjustments to remove or replace people for this schedule only.'}
      </p>
    </div>
  );
}

export default AuditScheduleConflicts;
